import React, { MutableRefObject, useState, useRef, useEffect, ComponentType, ReactNode, CSSProperties } from 'react';
import isNode from 'detect-node';

export function useIntersectionObserver(ref: MutableRefObject<Element | null>, options: IntersectionObserverInit = {}, forward: boolean = true) {
  const [element, setElement] = useState<Element | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const observer = useRef<null | IntersectionObserver>(null);

  const cleanOb = () => {
    if (observer.current) {
      observer.current.disconnect();
    }
  };

  useEffect(() => {
    setElement(ref.current);
  }, [ref]);

  useEffect(() => {
    if (!element) return;
    cleanOb();
    const ob = (observer.current = new IntersectionObserver(
      ([entry]) => {
        const isElementIntersecting = entry.isIntersecting;
        if (!forward) {
          setIsIntersecting(isElementIntersecting);
        } else if (forward && !isIntersecting && isElementIntersecting) {
          setIsIntersecting(isElementIntersecting);
          cleanOb();
        }
      },
      { ...options },
    ));
    ob.observe(element);
    return () => {
      cleanOb();
    };
    // eslint-disable-next-line
  }, [element, options]);

  return isIntersecting;
}

interface LazyComponentProps {
  tag?: ComponentType | keyof JSX.IntrinsicElements;
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  root?: Element | Document | null;
  threshold?: number | number[];
  rootMargin?: string;
  forward?: boolean;
}

interface IdleUntilUrgent {
  children: ReactNode;
  htmlElement: string;
}

export function LazyComponent(props: LazyComponentProps) {
  const { tag = 'div', children, style, className } = props;
  const SelectedComponent: any = tag;
  const ref = useRef<Element>(null);
  const isIntersecting = useIntersectionObserver(
    ref,
    {
      root: props.root ?? null,
      threshold: props.threshold ?? 0,
      rootMargin: props.rootMargin,
    },
    props.forward,
  );

  return <SelectedComponent ref={ref} style={style} className={className} children={isIntersecting ? children : null} />;
}

export const IdleUntilUrgent = (props: IdleUntilUrgent) => {
  const { children, htmlElement } = props;
  const [callbackId, setCallbackId] = useState(null);
  const [renderChild, setRenderChild] = useState(false);

  useEffect(() => {
    if (isNode) {
      // @ts-ignore
      if (typeof window.requestIdleCallback !== 'undefined') {
        // https://caniuse.com/#search=requestIdleCallback
        setCallbackId(
          // @ts-ignore
          window.requestIdleCallback(() => {
            setRenderChild(true);
          }),
        );
      } else {
        setTimeout(() => {
          setRenderChild(true);
        });
      }
    }
  }, []);

  if (!isNode && !renderChild) {
    // eslint-disable-next-line react/no-danger-with-children
    return React.createElement(
      htmlElement,
      {
        dangerouslySetInnerHTML: { __html: '' },
        suppressHydrationWarning: true,
        onClick: () => setRenderChild(true),
      },
      null,
    );
  }

  // Cancel the already scheduled render, if any
  if (!isNode && callbackId) {
    // @ts-ignore
    window.cancelIdleCallback(callbackId);
  }

  return children;
};
